import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";

const isChunkErr = (m) =>
  /chunkloaderror|loading chunk|loading css chunk|dynamically imported module|importing a module script/i.test(m || "");

const lazyRetry = (factory) =>
  lazy(() =>
    factory()
      .then((mod) => {
        sessionStorage.removeItem("msChunkReload");
        return mod;
      })
      .catch((err) => {
        if (isChunkErr(err && err.message) && !sessionStorage.getItem("msChunkReload")) {
          sessionStorage.setItem("msChunkReload", String(Date.now()));
          window.location.reload();
        }
        throw err;
      })
  );

const Consultation = lazyRetry(() => import("@/pages/Consultation"));
const Reports = lazyRetry(() => import("@/pages/Reports"));
const Blogs = lazyRetry(() => import("@/pages/Blogs"));
const Tools = lazyRetry(() => import("@/pages/Tools"));
const FreeKundli = lazyRetry(() => import("@/pages/FreeKundli"));
const BookingSuccess = lazyRetry(() => import("@/pages/BookingSuccess"));
const TrustedAstrologer = lazyRetry(() => import("@/pages/TrustedAstrologer"));
const JoinVSL = lazyRetry(() => import("@/pages/JoinVSL"));
const BookCheckout = lazyRetry(() => import("@/pages/BookCheckout"));
const AdminDashboard = lazyRetry(() => import("@/pages/AdminDashboard"));

const PageLoader = () => (
  <div className="min-h-[70vh] grid place-items-center bg-[#FAF7F2]" data-testid="page-loader">
    <div className="w-9 h-9 rounded-full border-[3px] border-[#E8B57A] border-t-[#561418] animate-spin" />
  </div>
);

class Boundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen grid place-items-center bg-[#FAF7F2] px-6 text-center" data-testid="error-boundary">
          <div>
            <p className="font-display text-2xl text-[#561418]">Something drifted out of alignment.</p>
            <button
              onClick={() => {
                sessionStorage.removeItem("msChunkReload");
                window.location.reload();
              }}
              data-testid="error-boundary-refresh"
              className="btn-gold mt-7 font-bold px-8 py-3.5 rounded-full"
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.getElementById(decodeURIComponent(hash.slice(1)));
        if (el) {
          if (window.__lenis) window.__lenis.scrollTo(el, { offset: -96, duration: 1.2 });
          else el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 350);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// VSL funnel pages render chrome-free: no site navbar/footer, no distractions.
function Shell({ children }) {
  const { pathname } = useLocation();
  const bare = ["/join", "/vsl-consultation", "/book", "/admin"].includes(pathname);
  return (
    <>
      {!bare && <Navbar />}
      {children}
      {!bare && <Footer />}
    </>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({
      lerp: 0.09,
      smoothWheel: true,
      prevent: (node) => !!(node && node.closest && node.closest('[data-radix-dialog-content], [role="dialog"], [data-lenis-ignore]')),
    });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    const onAnchor = (e) => {
      const a = e.target && e.target.closest && e.target.closest('a[href*="#"]');
      if (!a) return;
      const url = new URL(a.href, window.location.href);
      if (url.pathname === window.location.pathname && url.hash) {
        const el = document.getElementById(url.hash.slice(1));
        if (el) {
          e.preventDefault();
          history.replaceState(null, "", url.hash);
          lenis.scrollTo(el, { offset: -96, duration: 1.2 });
        }
      }
    };
    document.addEventListener("click", onAnchor);

    const onChunkFail = (e) => {
      const msg = String((e && e.reason && e.reason.message) || (e && e.message) || "");
      if (isChunkErr(msg) && !sessionStorage.getItem("msChunkReload")) {
        sessionStorage.setItem("msChunkReload", String(Date.now()));
        window.location.reload();
      }
    };
    window.addEventListener("unhandledrejection", onChunkFail);
    window.addEventListener("error", onChunkFail);

    return () => {
      document.removeEventListener("click", onAnchor);
      window.removeEventListener("unhandledrejection", onChunkFail);
      window.removeEventListener("error", onChunkFail);
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <Boundary>
      <BrowserRouter>
        <ScrollToTop />
        <Shell>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/the-call-consultation" element={<Consultation />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/blogs" element={<Blogs />} />
              <Route path="/blogs/:slug" element={<Blogs />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/free-kundli" element={<FreeKundli />} />
              <Route path="/booking-success" element={<BookingSuccess />} />
              <Route path="/trusted-astrologer" element={<TrustedAstrologer />} />
              <Route path="/join" element={<JoinVSL />} />
              <Route path="/vsl-consultation" element={<JoinVSL />} />
              <Route path="/book" element={<BookCheckout />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="*" element={<Home />} />
            </Routes>
          </Suspense>
        </Shell>
      </BrowserRouter>
    </Boundary>
  );
}
